import { useApolloClient } from "@apollo/client";
import {
  OvertimeLegalReasonEnum,
  PortalRotaDraftDocument,
  PortalRotaDraftQuery,
  PortalRotaDraftQueryVariables,
  ScheduleTypeEnum,
  usePortalEmployeeProfileSaveMutation,
  usePortalOvertimeRequestCreateMutation,
  usePortalRosterPublishMutation,
  usePortalRotaQuery,
} from "@dashboard/graphql";
import { Box, Button, Checkbox, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { DragEvent, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { ErrorText, Page, Section } from "../components";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { LegalReasonSelect } from "../OvertimeParts";
import { addDays, today, weekdayShort } from "../utils";

// A cell's plan: a shift, a day off, or the weekly base schedule.
type Window = { start: string; end: string };
type Value =
  | { kind: "shift"; shiftId: string }
  | { kind: "off" }
  | { kind: "base" }
  | { kind: "custom"; windows: Window[] };

const REASON = "Escala de sábados"; // backend rota.REASON: re-runs recognise their lines
const MODE_SAME_WEEK = "same_week";
const MODE_AVERAGE = "average";
const MODE_OVERTIME = "overtime";

const key = (userId: string, date: string) => `${userId}|${date}`;
const same = (a: Value, b: Value) =>
  a.kind === b.kind &&
  (a.kind !== "shift" || a.shiftId === (b as any).shiftId) &&
  (a.kind !== "custom" || JSON.stringify(a.windows) === JSON.stringify((b as any).windows));

const monthStart = (day: string) => `${day.slice(0, 7)}-01`;
const monthEnd = (day: string) => {
  const [y, mth] = day.split("-").map(Number);

  return new Date(Date.UTC(y, mth, 0)).toISOString().slice(0, 10);
};
const weekday = (day: string) => (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7; // 0 = Mon

const minutesOf = (start: string, end: string) => {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const span = eh * 60 + em - (sh * 60 + sm);

  return span > 0 ? span : span + 24 * 60;
};

/** Who works shifts (and so takes part in the rota): read-only until Edit. */
const ShiftPeople = ({
  profiles,
  onSaved,
}: {
  profiles: Array<{ userId: string; fullName: string; scheduleType: string }>;
  onSaved: () => void;
}) => {
  const intl = useIntl();
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [save, opts] = usePortalEmployeeProfileSaveMutation();
  const inShifts = profiles.filter(p => p.scheduleType === "SHIFTS");

  return (
    <Section
      title={intl.formatMessage(m.shiftPeopleTitle)}
      actions={
        !draft &&
        profiles.length > 0 && (
          <Button
            size="small"
            variant="secondary"
            onClick={() =>
              setDraft(
                Object.fromEntries(profiles.map(p => [p.userId, p.scheduleType === "SHIFTS"])),
              )
            }
          >
            {intl.formatMessage(m.edit)}
          </Button>
        )
      }
    >
      {profiles.length === 0 ? (
        <Text size={2} color="default2">
          {intl.formatMessage(m.noProfilesYet)}
        </Text>
      ) : !draft ? (
        <Text size={3}>
          {inShifts.length
            ? inShifts.map(p => p.fullName).join(", ")
            : intl.formatMessage(m.nobodyInShifts)}
        </Text>
      ) : (
        <>
          <Box display="flex" gap={4} flexWrap="wrap">
            {profiles.map(profile => (
              <Checkbox
                key={profile.userId}
                checked={draft[profile.userId]}
                onCheckedChange={checked =>
                  setDraft({ ...draft, [profile.userId]: checked === true })
                }
              >
                <Text size={2}>{profile.fullName}</Text>
              </Checkbox>
            ))}
          </Box>
          <Text size={1} color="default2">
            {intl.formatMessage(m.shiftPeopleHint)}
          </Text>
          {errors.map(error => (
            <Text key={error} size={2} color="critical1">
              {error}
            </Text>
          ))}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button size="small" variant="secondary" onClick={() => setDraft(null)}>
              {intl.formatMessage(m.cancel)}
            </Button>
            <Button
              size="small"
              variant="primary"
              disabled={opts.loading}
              onClick={async () => {
                const problems: string[] = [];

                for (const profile of profiles) {
                  const wanted = draft[profile.userId];

                  if (wanted === (profile.scheduleType === "SHIFTS")) {
                    continue;
                  }

                  const result = await save({
                    variables: {
                      userId: profile.userId,
                      input: {
                        scheduleType: wanted ? ScheduleTypeEnum.SHIFTS : ScheduleTypeEnum.FIXED,
                      },
                    },
                  });

                  (result.data?.employeeProfileSave?.errors ?? []).forEach(error =>
                    problems.push(`${profile.fullName}: ${error.message}`),
                  );
                }
                setErrors(problems);

                if (!problems.length) {
                  setDraft(null);
                }

                onSaved();
              }}
            >
              {intl.formatMessage(m.save)}
            </Button>
          </Box>
        </>
      )}
    </Section>
  );
};

export const RotaPlanner = () => {
  const intl = useIntl();
  const tomorrow = addDays(today(), 1);
  const [params, setParams] = useState({
    start: monthStart(addDays(monthEnd(today()), 1)),
    end: monthEnd(addDays(monthEnd(today()), 1)),
    shiftId: "",
    minPerSaturday: "1",
    mode: MODE_AVERAGE,
    userIds: null as string[] | null, // null = everyone on shifts
  });
  const [view, setView] = useState(monthStart(params.start));
  const viewEnd = monthEnd(view);
  const { data, refetch } = usePortalRotaQuery({
    variables: { start: view, end: viewEnd },
    fetchPolicy: "cache-and-network",
  });
  const client = useApolloClient();
  const [generated, setGenerated] = useState<{
    loading: boolean;
    data?: PortalRotaDraftQuery;
  }>({ loading: false });
  const generate = async (options: { variables: PortalRotaDraftQueryVariables }) => {
    setGenerated({ loading: true });

    try {
      const result = await client.query<PortalRotaDraftQuery, PortalRotaDraftQueryVariables>({
        query: PortalRotaDraftDocument,
        variables: options.variables,
        fetchPolicy: "network-only",
      });

      setGenerated({ loading: false, data: result.data });

      return result;
    } catch (error) {
      setGenerated({ loading: false });
      throw error;
    }
  };
  const [draft, setDraft] = useState<Record<string, Value>>({});
  const [dragged, setDragged] = useState<{ userId: string; date: string } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [legalReason, setLegalReason] = useState<OvertimeLegalReasonEnum | "">("");
  const [ordered, setOrdered] = useState<{ count: number; errors: string[] } | null>(null);
  const [orderOvertime] = usePortalOvertimeRequestCreateMutation();
  const [publish, publishOpts] = usePortalRosterPublishMutation({
    onCompleted: result => {
      if (!result.rosterPublish?.errors.length) {
        setDraft({});
        refetch();
      }
    },
  });

  // Overtime mode: each Saturday published is ordered as overtime, with the
  // chosen legal reason; the colaborador then acknowledges it in the portal.
  const orderSaturdays = async (
    lines: Array<{ userId: string; date: string; minutes: number }>,
  ) => {
    const errors: string[] = [];
    let count = 0;

    for (const line of lines) {
      const result = await orderOvertime({
        variables: {
          userId: line.userId,
          date: line.date,
          minutes: line.minutes,
          reason: REASON,
          legalReason: legalReason || undefined,
        },
      });
      const problems = result.data?.overtimeRequestCreate?.errors ?? [];

      if (problems.length) {
        errors.push(
          `${nameOf(line.userId)} ${line.date}: ${problems.map(e => e.message).join(" ")}`,
        );
      } else {
        count += 1;
      }
    }
    setOrdered({ count, errors });
  };

  const shifts = (data?.shifts ?? []).filter(shift => shift.isActive);
  const shiftById = new Map((data?.shifts ?? []).map(shift => [shift.id, shift]));
  const workers = (data?.employeeProfiles ?? []).filter(p => p.scheduleType === "SHIFTS");
  const saturdayShiftId = params.shiftId || shifts[0]?.id || "";
  const selected = params.userIds ?? workers.map(w => w.userId);
  const days = useMemo(() => {
    const out: string[] = [];

    for (let day = view; day <= viewEnd; day = addDays(day, 1)) {
      out.push(day);
    }

    return out;
  }, [view, viewEnd]);

  const published = new Map<string, Value>(
    (data?.rosterBoard ?? []).map(line => [
      key(line.userId, line.date),
      line.followsBase
        ? { kind: "base" }
        : line.customWindows.length
          ? {
              kind: "custom",
              windows: line.customWindows.map(({ start, end }) => ({ start, end })),
            }
          : line.shift
            ? { kind: "shift", shiftId: line.shift.id }
            : { kind: "off" },
    ]),
  );
  const valueOf = (userId: string, date: string): Value =>
    draft[key(userId, date)] ?? published.get(key(userId, date)) ?? { kind: "base" };
  const absenceOf = (userId: string, date: string) =>
    (data?.absenceCalendar ?? []).find(
      a => a.userId === userId && a.startDate <= date && date <= a.endDate,
    );
  const changes = Object.entries(draft).filter(([k, value]) => {
    const current = published.get(k) ?? { kind: "base" };

    return !same(current, value);
  });

  const baseMinutes = (worker: (typeof workers)[number], date: string) => {
    const rows = worker.weeklySchedule.filter(row => row.weekday === weekday(date));
    const total = rows.reduce((sum, row) => sum + minutesOf(row.start, row.end), 0);

    return Math.min(total, worker.dailyHours * 60);
  };
  const plannedMinutes = (worker: (typeof workers)[number], date: string) => {
    const value = valueOf(worker.userId, date);

    if (value.kind === "off") {
      return 0;
    }

    if (value.kind === "shift") {
      const shift = shiftById.get(value.shiftId);

      return shift ? minutesOf(shift.startTime, shift.endTime) - shift.breakMinutes : 0;
    }

    if (value.kind === "custom") {
      return value.windows.reduce((sum, w) => sum + minutesOf(w.start, w.end), 0);
    }

    return baseMinutes(worker, date);
  };

  const set = (userId: string, date: string, value: Value) =>
    setDraft(current => ({ ...current, [key(userId, date)]: value }));

  const onDrop = (event: DragEvent, userId: string, date: string) => {
    event.preventDefault();

    if (!dragged || date < tomorrow) {
      return;
    }

    const source = valueOf(dragged.userId, dragged.date);
    const target = valueOf(userId, date);

    if (dragged.userId === userId && dragged.date === date) {
      return;
    }

    if (dragged.userId === userId) {
      // Along the row: the plan moves to another day, the old day goes back
      // to the normal schedule.
      set(userId, date, source);
      set(userId, dragged.date, { kind: "base" });
    } else {
      // Up or down: hand it to a colleague (swap their plans).
      set(userId, date, source);
      set(dragged.userId, dragged.date, dragged.date === date ? target : { kind: "base" });
    }

    setDragged(null);
  };

  const label = (value: Value) => {
    if (value.kind === "shift") {
      return shiftById.get(value.shiftId)?.name ?? "?";
    }

    if (value.kind === "custom") {
      return intl.formatMessage(m.adjustedUntil, {
        time: value.windows[value.windows.length - 1]?.end ?? "",
      });
    }

    return value.kind === "off" ? intl.formatMessage(l.dayOffOption) : "";
  };

  const cellOptions: Option[] = [
    { value: "base", label: intl.formatMessage(m.legendBase) },
    { value: "off", label: intl.formatMessage(l.dayOffOption) },
    ...shifts.map(shift => ({ value: shift.id, label: shift.name })),
  ];
  const modeOptions: Option[] = [
    { value: MODE_AVERAGE, label: intl.formatMessage(m.modeAverage) },
    { value: MODE_SAME_WEEK, label: intl.formatMessage(m.modeSameWeek) },
    { value: MODE_OVERTIME, label: intl.formatMessage(m.modeOvertime) },
  ];
  const shiftOptions: Option[] = shifts.map(shift => ({ value: shift.id, label: shift.name }));
  const setPeriod = (start: string, end: string) => {
    setParams({ ...params, start, end });
    setView(monthStart(start));
  };
  const nextMonth = addDays(monthEnd(today()), 1);
  const warnings = generated.data?.rotaDraft?.warnings ?? [];
  const nameOf = (userId?: string | null) => workers.find(w => w.userId === userId)?.fullName ?? "";

  return (
    <Page>
      <ShiftPeople profiles={data?.employeeProfiles ?? []} onSaved={() => refetch()} />
      <Section title={intl.formatMessage(m.shiftsPlannerModule)}>
        <Text size={2} color="default2">
          {intl.formatMessage(m.rotaIntro)}
        </Text>
        {workers.length === 0 ? (
          <Text size={3} color="warning1">
            {intl.formatMessage(m.noShiftWorkers)}
          </Text>
        ) : shifts.length === 0 ? (
          <Text size={3} color="warning1">
            {intl.formatMessage(m.noSaturdayShift)}
          </Text>
        ) : (
          <>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                size="small"
                variant="tertiary"
                onClick={() => {
                  const monday = addDays(tomorrow, (7 - weekday(tomorrow)) % 7);

                  setPeriod(monday, addDays(monday, 6));
                }}
              >
                {intl.formatMessage(m.presetNextWeek)}
              </Button>
              <Button
                size="small"
                variant="tertiary"
                onClick={() => setPeriod(nextMonth, monthEnd(nextMonth))}
              >
                {intl.formatMessage(m.presetNextMonth)}
              </Button>
              <Button
                size="small"
                variant="tertiary"
                onClick={() => setPeriod(tomorrow, `${tomorrow.slice(0, 4)}-12-31`)}
              >
                {intl.formatMessage(m.presetRestOfYear)}
              </Button>
              <Button
                size="small"
                variant="tertiary"
                onClick={() => {
                  const year = Number(tomorrow.slice(0, 4)) + 1;

                  setPeriod(`${year}-01-01`, `${year}-12-31`);
                }}
              >
                {intl.formatMessage(m.presetNextYear)}
              </Button>
            </Box>
            <Box
              display="grid"
              gap={2}
              __gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))"
              alignItems="end"
            >
              <Input
                size="small"
                type="date"
                label={intl.formatMessage(m.start)}
                value={params.start}
                min={tomorrow}
                onChange={e => setParams({ ...params, start: e.target.value })}
              />
              <Input
                size="small"
                type="date"
                label={intl.formatMessage(m.end)}
                value={params.end}
                min={params.start}
                onChange={e => setParams({ ...params, end: e.target.value })}
              />
              <Select
                size="small"
                label={intl.formatMessage(m.saturdayShift)}
                options={shiftOptions}
                value={shiftOptions.find(o => o.value === saturdayShiftId) ?? null}
                onChange={option => setParams({ ...params, shiftId: option.value })}
              />
              <Input
                size="small"
                type="number"
                min={1}
                max={workers.length}
                label={intl.formatMessage(m.minPerSaturday)}
                value={params.minPerSaturday}
                onChange={e => setParams({ ...params, minPerSaturday: e.target.value })}
              />
            </Box>
            <Select
              size="small"
              label={intl.formatMessage(m.compensationMode)}
              options={modeOptions}
              value={modeOptions.find(o => o.value === params.mode) ?? null}
              onChange={option => setParams({ ...params, mode: option.value })}
            />
            {params.mode === MODE_OVERTIME && (
              <Box __maxWidth="420px">
                <LegalReasonSelect value={legalReason} onChange={setLegalReason} />
              </Box>
            )}
            {params.mode === MODE_AVERAGE && (
              <Text size={2} color="warning1">
                {intl.formatMessage(m.adaptabilityHint)}
              </Text>
            )}
            <Box display="flex" gap={4} flexWrap="wrap">
              {workers.map(worker => (
                <Checkbox
                  key={worker.userId}
                  checked={selected.includes(worker.userId)}
                  onCheckedChange={checked =>
                    setParams({
                      ...params,
                      userIds: checked
                        ? [...selected, worker.userId]
                        : selected.filter(id => id !== worker.userId),
                    })
                  }
                >
                  <Text size={2}>{worker.fullName}</Text>
                </Checkbox>
              ))}
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <Button
                variant="primary"
                size="small"
                disabled={
                  !saturdayShiftId ||
                  selected.length === 0 ||
                  params.end < params.start ||
                  generated.loading
                }
                onClick={async () => {
                  const result = await generate({
                    variables: {
                      start: params.start,
                      end: params.end,
                      saturdayShiftId,
                      minPerSaturday: Number(params.minPerSaturday) || 1,
                      mode: params.mode,
                      userIds: selected,
                    },
                  });
                  const cells = result.data?.rotaDraft?.cells ?? [];

                  setDraft(
                    Object.fromEntries(
                      cells.map(cell => [
                        key(cell.userId, cell.date),
                        cell.kind === "saturday" && cell.shift
                          ? { kind: "shift", shiftId: cell.shift.id }
                          : cell.kind === "day_off"
                            ? { kind: "off" }
                            : cell.kind === "short"
                              ? {
                                  kind: "custom",
                                  windows: cell.windows.map(({ start, end }) => ({ start, end })),
                                }
                              : { kind: "base" },
                      ]),
                    ) as Record<string, Value>,
                  );
                  setView(monthStart(params.start));
                }}
              >
                {intl.formatMessage(m.generate)}
              </Button>
              <Text size={2} color="default2">
                {intl.formatMessage(m.generateHint)}
              </Text>
            </Box>
            {(generated.data?.rotaDraft?.averages ?? []).map(average => (
              <Text key={average.userId} size={2}>
                {intl.formatMessage(m.averageLine, {
                  name: nameOf(average.userId),
                  hours: average.hours,
                  weeks: average.weeks,
                  pending: average.pendingHours,
                })}
              </Text>
            ))}
            {warnings.map((warning, index) => (
              <Text
                key={index}
                size={2}
                color={
                  warning.code === "holiday" || warning.code === "credit_left"
                    ? "default2"
                    : "warning1"
                }
              >
                {warning.userId ? `${nameOf(warning.userId)}: ` : ""}
                {warning.message}
              </Text>
            ))}
          </>
        )}
      </Section>

      {workers.length > 0 && (
        <Section
          title={new Date(`${view}T12:00:00Z`).toLocaleDateString(intl.locale, {
            month: "long",
            year: "numeric",
            timeZone: "UTC",
          })}
          actions={
            <Box display="flex" gap={2}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => setView(monthStart(addDays(view, -1)))}
              >
                ‹
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => setView(monthStart(addDays(viewEnd, 1)))}
              >
                ›
              </Button>
            </Box>
          }
        >
          <Text size={2} color="default2">
            {intl.formatMessage(m.dragHint)}
          </Text>
          <Box __overflowX="auto">
            <table className="portal-rota">
              <thead>
                <tr>
                  <th />
                  {days.map(day => (
                    <th key={day} className={weekday(day) >= 5 ? "portal-rota-weekend" : undefined}>
                      <div>{weekdayShort(intl.locale, weekday(day))}</div>
                      <div>{Number(day.slice(8))}</div>
                    </th>
                  ))}
                  <th>{intl.formatMessage(m.hoursPerWeek)}</th>
                </tr>
              </thead>
              <tbody>
                {workers.map(worker => {
                  // Planned hours of each Mon-Sun week touching this month.
                  const weeks = new Map<string, number>();

                  for (
                    let day = addDays(view, -weekday(view));
                    day <= viewEnd;
                    day = addDays(day, 1)
                  ) {
                    const monday = addDays(day, -weekday(day));

                    weeks.set(monday, (weeks.get(monday) ?? 0) + plannedMinutes(worker, day));
                  }

                  return (
                    <tr key={worker.userId}>
                      <th className="portal-rota-name">{worker.fullName}</th>
                      {days.map(day => {
                        const k = key(worker.userId, day);
                        const value = valueOf(worker.userId, day);
                        const absence = absenceOf(worker.userId, day);
                        const pendingChange =
                          k in draft && !same(published.get(k) ?? { kind: "base" }, value);
                        const past = day < tomorrow;
                        const color =
                          value.kind === "shift" ? shiftById.get(value.shiftId)?.color : undefined;
                        const classes = [
                          "portal-rota-cell",
                          weekday(day) >= 5 ? "portal-rota-weekend" : "",
                          value.kind === "off" ? "portal-rota-off" : "",
                          value.kind === "custom" ? "portal-rota-short" : "",
                          pendingChange ? "portal-rota-draft" : "",
                          past ? "portal-rota-past" : "",
                          absence ? "portal-rota-absent" : "",
                        ].join(" ");

                        return (
                          <td
                            key={day}
                            className={classes}
                            style={color ? { background: color } : undefined}
                            title={absence?.label ?? undefined}
                            draggable={!past && value.kind !== "base"}
                            onDragStart={() => setDragged({ userId: worker.userId, date: day })}
                            onDragOver={event => !past && event.preventDefault()}
                            onDrop={event => onDrop(event, worker.userId, day)}
                            onClick={() => !past && setEditing(k)}
                          >
                            {editing === k ? (
                              <select
                                autoFocus
                                value={
                                  value.kind === "shift"
                                    ? value.shiftId
                                    : value.kind === "custom"
                                      ? "base"
                                      : value.kind
                                }
                                onBlur={() => setEditing(null)}
                                onChange={event => {
                                  const choice = event.target.value;

                                  set(
                                    worker.userId,
                                    day,
                                    choice === "base" || choice === "off"
                                      ? { kind: choice }
                                      : { kind: "shift", shiftId: choice },
                                  );
                                  setEditing(null);
                                }}
                              >
                                {cellOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span>
                                {label(value) || (absence ? absence.label.slice(0, 3) : "")}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="portal-rota-hours">
                        {[...weeks.values()]
                          .map(minutes => {
                            const hours = Math.round(minutes / 6) / 10;

                            return hours;
                          })
                          .map((hours, index) => (
                            <span
                              key={index}
                              className={
                                hours > (params.mode === MODE_SAME_WEEK ? 40 : 50)
                                  ? "portal-rota-over"
                                  : undefined
                              }
                            >
                              {hours}
                            </span>
                          ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Text size={1} color="default2">
              <span className="portal-rota-legend portal-rota-off" />{" "}
              {intl.formatMessage(l.dayOffOption)}
            </Text>
            <Text size={1} color="default2">
              <span className="portal-rota-legend portal-rota-draft" />{" "}
              {intl.formatMessage(m.legendDraft)}
            </Text>
            <Text size={1} color="default2">
              <span className="portal-rota-legend portal-rota-absent" />{" "}
              {intl.formatMessage(m.legendAbsent)}
            </Text>
            <Text size={1} color="default2">
              <span className="portal-rota-legend portal-rota-short" />{" "}
              {intl.formatMessage(m.legendShort)}
            </Text>
          </Box>
          <Box display="flex" gap={2} alignItems="center" justifyContent="flex-end">
            <Text size={2} color={changes.length ? "warning1" : "default2"}>
              {intl.formatMessage(m.draftChanges, { count: changes.length })}
            </Text>
            <Button
              size="small"
              variant="secondary"
              disabled={!changes.length}
              onClick={() => setDraft({})}
            >
              {intl.formatMessage(m.discard)}
            </Button>
            <Button
              size="small"
              variant="primary"
              disabled={
                !changes.length ||
                publishOpts.loading ||
                (params.mode === MODE_OVERTIME && !legalReason)
              }
              onClick={async () => {
                const saturdays = changes
                  .map(([k, value]) => ({ key: k.split("|"), value }))
                  .filter(({ key, value }) => value.kind === "shift" && weekday(key[1]) === 5)
                  .map(({ key, value }) => {
                    const shift = value.kind === "shift" ? shiftById.get(value.shiftId) : undefined;

                    return {
                      userId: key[0],
                      date: key[1],
                      minutes: shift
                        ? minutesOf(shift.startTime, shift.endTime) - shift.breakMinutes
                        : 0,
                    };
                  });
                const result = await publish({
                  variables: {
                    reason: REASON,
                    lines: changes.map(([k, value]) => {
                      const [userId, date] = k.split("|");

                      return {
                        userId,
                        date,
                        shiftId: value.kind === "shift" ? value.shiftId : null,
                        followsBase: value.kind === "base",
                        customWindows: value.kind === "custom" ? value.windows : undefined,
                      };
                    }),
                  },
                });

                if (
                  params.mode === MODE_OVERTIME &&
                  !result.data?.rosterPublish?.errors.length &&
                  saturdays.length
                ) {
                  await orderSaturdays(saturdays);
                }
              }}
            >
              {intl.formatMessage(m.publishRoster)}
            </Button>
          </Box>
          {publishOpts.data?.rosterPublish && !publishOpts.data.rosterPublish.errors.length && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Text size={2} color="success1">
                {intl.formatMessage(m.rosterSaved, {
                  count: publishOpts.data.rosterPublish.lines.length,
                })}
              </Text>
              {publishOpts.data.rosterPublish.lines.some(line => line.lateNotice) && (
                <Text size={2} color="warning1">
                  {intl.formatMessage(l.lateNoticeWarning)}
                </Text>
              )}
              {publishOpts.data.rosterPublish.warnings.map((w, i) => (
                <Text key={i} size={2} color="warning1">
                  {w.date}: {w.message}
                </Text>
              ))}
            </Box>
          )}
          <ErrorText errors={publishOpts.data?.rosterPublish?.errors} />
          {ordered && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Text size={2} color="success1">
                {intl.formatMessage(m.overtimeOrdersCreated, { count: ordered.count })}
              </Text>
              {ordered.errors.map(error => (
                <Text key={error} size={2} color="critical1">
                  {error}
                </Text>
              ))}
            </Box>
          )}
        </Section>
      )}
    </Page>
  );
};
